import currency from 'currency.js';

import {
  CartItem,
  CartItemTypeEnum,
} from '@sola/schema/dotcom/cart/cart-item.ts';
import { Cart } from '@sola/schema/dotcom/cart/cart.ts';
import { CommitmentFrequencyEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import { ErrorCart } from '@sola/schema/dotcom/cart/errors.ts';
import { isItemFund } from '@sola/shared-utils/cart/cart-item-type-guards.ts';
import {
  userErrorCartItemMaxExceeded,
  userErrorCartItemMinSubceeded,
  userErrorCartMaxExceeded,
} from '@sola/shared-utils/errors/cart.ts';
import { isValueInRange } from '@sola/utils/number.ts';

export const commitmentFrequencyFactor = (
  commitmentFrequency: CommitmentFrequencyEnum,
): number => {
  const factors = {
    [CommitmentFrequencyEnum.OneTime]: 1,
    [CommitmentFrequencyEnum.Monthly]: 1,
    [CommitmentFrequencyEnum.Quarterly]: undefined,
    [CommitmentFrequencyEnum.Semiannually]: undefined,
    // [CommitmentFrequencyEnum.Quarterly]: 3,
    // [CommitmentFrequencyEnum.Semiannually]: 6,
    [CommitmentFrequencyEnum.Annually]: 12,
  };
  return factors[commitmentFrequency] || 1;
};

export const calculateAmount = (unitPrice: number, quantity: number) => {
  return currency(unitPrice).multiply(quantity ?? 1).value;
};

/**
 * @description Round decimal numbers to two digits - mitigates the toFixed and other JS rounding bugs (1.005 = 1.01 and 6.005 = 6.01 as examples)
 */
export const formatNumberAsCurrency = (value: number) => {
  return currency(value).value;
};

/**
 * @description Format number as currency string - consistent two-digit value without currency characters (comma, symbol)
 */
export const formatNumberAsCurrencyString = (value: number) => {
  return currency(value, {
    format: (curr) => {
      return `${curr?.dollars() ?? 0}.${String(curr?.cents() ?? 0).padStart(2, '0')}`;
    },
  }).format();
};

export type TotalsCollection = {
  totalToday: currency;
  totalOneTime: currency;
  totalMonthly: currency;
  totalYearly: currency;
};

export const calculateTotalsReducer = (
  acc: TotalsCollection,
  { amount, commitmentFrequency, excludeFromToday }: CartItem,
): TotalsCollection => {
  switch (commitmentFrequency) {
    case CommitmentFrequencyEnum.OneTime:
      acc.totalOneTime = acc.totalOneTime.add(amount);
      acc.totalToday = acc.totalToday.add(amount);
      return acc;
    case CommitmentFrequencyEnum.Monthly:
      acc.totalMonthly = acc.totalMonthly.add(amount);
      acc.totalToday = acc.totalToday.add(amount);
      return acc;
    case CommitmentFrequencyEnum.Quarterly:
      // TODO: acc.totalQuarterly ???
      // acc.totalToday += amount;
      return acc;
    case CommitmentFrequencyEnum.Semiannually:
      // TODO: acc.totalSemianually ???
      // acc.totalToday += amount;
      return acc;
    case CommitmentFrequencyEnum.Annually:
      acc.totalYearly = acc.totalYearly.add(amount);
      acc.totalToday = !excludeFromToday
        ? acc.totalToday.add(amount)
        : acc.totalToday.add(0);
      return acc;
    default:
      return acc;
  }
};

const getValueOutOfRangeError = ({
  id,
  amount,
  unitPrice,
  minLimit,
  maxLimit,
}: {
  id: string;
  amount: number;
  unitPrice: number;
  minLimit: number;
  maxLimit: number;
}) => {
  if (unitPrice < minLimit) {
    return userErrorCartItemMinSubceeded({
      id,
      amount,
      minLimit,
    });
  }

  return userErrorCartItemMaxExceeded({
    id,
    amount,
    maxLimit,
  });
};

export const validateItemGivingAmount = (
  cartItem: CartItem,
  maxLimit: number,
): ErrorCart | null => {
  const { itemType, unitPrice } = cartItem;

  if (cartItem.amount > maxLimit) {
    return userErrorCartItemMaxExceeded({
      id: cartItem.id,
      amount: cartItem.amount,
      // cartItem,
      maxLimit,
    });
  }

  switch (itemType) {
    case CartItemTypeEnum.Fund:
    case CartItemTypeEnum.Gift: {
      if (!cartItem.limits) {
        return null;
      }

      // TODO: To fully support gifts, we'll eventually need the different limits per frequency
      const { min, max } = cartItem.limits;
      if (
        // Monthly fund commitments only check the global minimum
        isItemFund(cartItem) &&
        cartItem.commitmentFrequency === CommitmentFrequencyEnum.Monthly
      ) {
        if (!isValueInRange(unitPrice, cartItem.limits.defaultMin, max)) {
          return getValueOutOfRangeError({
            id: cartItem.id,
            amount: cartItem.amount,
            unitPrice,
            minLimit: cartItem.limits.defaultMin,
            maxLimit: max,
          });
        }

        // Monthly fund commitments that are above the default min are good to go
        return null;
      }
      if (!isValueInRange(unitPrice, min, max)) {
        return getValueOutOfRangeError({
          id: cartItem.id,
          amount: cartItem.amount,
          unitPrice,
          minLimit: min,
          maxLimit: max,
        });
      }
    }
  }

  return null;
};

/**
 * Check if the cart total is above the max allowed value
 */
const validateCartMax = (cart: Cart, maxLimit: number): ErrorCart | null => {
  const { id, totalToday } = cart;
  if (totalToday > maxLimit) {
    return userErrorCartMaxExceeded({
      maxLimit,
      cartId: id,
      total: totalToday,
    });
  }

  return null;
};

/**
 * Validate a cart item's donation amount against the cart total max and a potential respective
 * item max
 */
export const validateGivingAmount = (
  cart: Cart,
  maxLimit: number,
): ErrorCart | ErrorCart[] | null => {
  const errors = cart.items.reduce((itemErrors: ErrorCart[], item) => {
    const vError = validateItemGivingAmount(item, maxLimit);
    if (vError) {
      return [...itemErrors, vError];
    }

    return itemErrors;
  }, []);

  if (!errors.length) {
    // Only adding cart max error if no item errors - first clean-up item errors
    const cartMaxError = validateCartMax(cart, maxLimit);
    if (cartMaxError) {
      errors.push(cartMaxError);
    }
  }

  return errors.length ? errors : null;
};
